import { EApplicantType, EHomeStatus, getEHomeStatusList } from '@if/core/types'
import { ControlledNumberField, ControlledSelect, ControlledTextField, useTranslatedOptionEnum } from '@if/mui'
import { Divider, Grid, Stack, Tooltip, Typography } from '@mui/material'
import Fieldset from '@src/components/common/Fieldset'
import { ApplicantExpenses } from '@src/types/CreditApplicationSchema'
import { useEffect, useState } from 'react'
import { Control, FieldError, FieldErrorsImpl, Merge, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch: UseFormWatch<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>
  errors?: Merge<FieldError, FieldErrorsImpl<ApplicantExpenses>>
  applicantType: EApplicantType
  showDetailedForm: boolean
  disabled: boolean
  toolTip: string
}

const ApplicantExpensesSection = ({
  control,
  watch,
  setValue,
  applicantType,
  showDetailedForm,
  disabled,
  toolTip,
}: Props) => {
  const { t } = useTranslation()
  const monthlyOtherExpensePayment = String(watch(`${applicantType}.expenses.monthlyOtherExpensePayment`))
  const houseFeeType = String(watch(`${applicantType}.expenses.homeFeeTypeId`)) as EHomeStatus
  const [isHouseMarketLocked, setIsHouseMarketLocked] = useState<boolean>(false)
  const [isOwner, setIsOwner] = useState<boolean>(false)

  useEffect(() => {
    if (houseFeeType === EHomeStatus.Owner) {
      setIsHouseMarketLocked(false)
      setIsOwner(true)
    } else if (
      houseFeeType === EHomeStatus.WithRelative ||
      houseFeeType === EHomeStatus.Renter ||
      houseFeeType === EHomeStatus.ReserveHousing
    ) {
      setValue(`${applicantType}.expenses.houseMarketValue`, undefined, { shouldValidate: true })
      setIsHouseMarketLocked(true)
      setIsOwner(false)
    } else if (houseFeeType) {
      setIsHouseMarketLocked(false)
      setIsOwner(false)
    }
  }, [houseFeeType, applicantType, setValue])

  const isInvalidOtherExpensePayment =
    !monthlyOtherExpensePayment ||
    monthlyOtherExpensePayment.toString().trim() === '' ||
    monthlyOtherExpensePayment.toString().trim() === '0'
  useEffect(() => {
    if (isInvalidOtherExpensePayment) {
      setValue(`${applicantType}.expenses.otherExpensePaymentDescription`, '')
    }
  }, [isInvalidOtherExpensePayment, monthlyOtherExpensePayment, applicantType, setValue])

  const homeStatusOptions = useTranslatedOptionEnum('EHomeStatus', getEHomeStatusList(), 'enum')

  return (
    <>
      <Divider>
        <Typography variant="subtitle1">
          {t('editCreditApplication.expenses.recurringMonthlyHomeRelatedDisbursements')}
        </Typography>
      </Divider>
      <Fieldset label={t('editCreditApplication.expenses.homeFeesLebel')}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 1 }}>
          <Tooltip title={toolTip}>
            <ControlledSelect
              name={`${applicantType}.expenses.homeFeeTypeId`}
              control={control}
              label={t('editCreditApplication.expenses.homeFeeTypeId')}
              disabled={disabled}
              fullWidth
              disableEmptyOption
              options={homeStatusOptions}
            />
          </Tooltip>
          {!isOwner && (
            <Tooltip title={toolTip}>
              <ControlledNumberField
                id="monthlyHomePayment"
                name={`${applicantType}.expenses.housingMonthly`}
                control={control}
                label={t('editCreditApplication.expenses.monthlyHomePayment')}
                disabled={disabled}
                disableDecimals
                fullWidth
              />
            </Tooltip>
          )}
          <Tooltip title={toolTip}>
            <ControlledNumberField
              id="houseMarketValue"
              name={`${applicantType}.expenses.houseMarketValue`}
              control={control}
              disabled={isHouseMarketLocked || disabled}
              label={`${t('editCreditApplication.expenses.houseMarketValue')} (${String(t('common.ifApplicable'))})`}
              disableDecimals
              fullWidth
            />
          </Tooltip>
        </Stack>
      </Fieldset>
      {showDetailedForm && (
        <>
          <Fieldset label={t('editCreditApplication.expenses.others')}>
            <Grid container spacing={1} rowSpacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyOwnerInsuranceFee"
                  name={`${applicantType}.expenses.monthlyOwnerInsuranceFee`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyOwnerInsuranceFee')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyMortgageInsuranceFee"
                  name={`${applicantType}.expenses.monthlyMortgageInsuranceFee`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyMortgageInsuranceFee')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyMunicipalAndSchoolTaxPayment"
                  name={`${applicantType}.expenses.monthlyMunicipalAndSchoolTaxPayment`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyMunicipalAndSchoolTaxPayment')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyCondominiumFee"
                  name={`${applicantType}.expenses.monthlyCondominiumFee`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyCondominiumFee')}
                  disabled={disabled}
                  isMoney
                  fullWidth
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyPublicServiceFee"
                  name={`${applicantType}.expenses.monthlyPublicServiceFee`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyPublicServiceFee')}
                  disabled={disabled}
                  isMoney
                  fullWidth
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyRrspRefund"
                  name={`${applicantType}.expenses.monthlyRrspRefund`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyRrspRefund')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyOtherHomeFee"
                  name={`${applicantType}.expenses.monthlyOtherHomeFee`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyOtherHomeFee')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
            </Grid>
          </Fieldset>
          <Divider>
            <Typography variant="subtitle1">
              {t('editCreditApplication.expenses.monthlyDisbursementsWithCreditAgreement')}
            </Typography>
          </Divider>
          <Fieldset label={t('editCreditApplication.expenses.loanAndCreditLabel')}>
            <Grid container spacing={1} rowSpacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyInstallmentLoanPayment"
                  name={`${applicantType}.expenses.monthlyInstallmentLoanPayment`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyInstallmentLoanPayment')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyStudentLoanPayment"
                  name={`${applicantType}.expenses.monthlyStudentLoanPayment`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyStudentLoanPayment')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyLineOfCreditPayment"
                  name={`${applicantType}.expenses.monthlyLineOfCreditPayment`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyLineOfCreditPayment')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyCreditCardsPayment"
                  name={`${applicantType}.expenses.monthlyCreditCardsPayment`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyCreditCardsPayment')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyPaydayLoanPayment"
                  name={`${applicantType}.expenses.monthlyPaydayLoanPayment`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyPaydayLoanPayment')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyPersonalLoanPayment"
                  name={`${applicantType}.expenses.monthlyPersonalLoanPayment`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyPersonalLoanPayment')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <ControlledNumberField
                  id="monthlyOtherLoanPayment"
                  name={`${applicantType}.expenses.monthlyOtherLoanPayment`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyOtherLoanPayment')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
            </Grid>
          </Fieldset>
          <Fieldset label={t('editCreditApplication.expenses.others')}>
            <Grid container spacing={1} rowSpacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <ControlledNumberField
                  id="monthlyOtherExpensePayment"
                  name={`${applicantType}.expenses.monthlyOtherExpensePayment`}
                  control={control}
                  label={t('editCreditApplication.expenses.monthlyOtherExpensePayment')}
                  disabled={disabled}
                  fullWidth
                  isMoney
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 8,
                }}
              >
                <ControlledTextField
                  id="otherExpensePaymentDescription"
                  name={`${applicantType}.expenses.otherExpensePaymentDescription`}
                  control={control}
                  disabled={isInvalidOtherExpensePayment || disabled}
                  label={t('editCreditApplication.expenses.otherExpensePaymentDescription')}
                  slotProps={{ htmlInput: { maxLength: 50 } }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Fieldset>
        </>
      )}
    </>
  )
}

export default ApplicantExpensesSection
